<template>
    <PageLayout>
        <div class="checkout-container">
            <div v-if="!user" class="login-required">
                <h2>Device Checkout</h2>
                <p>Please login to access device information</p>
                <div class="login-button-container">
                    <LoginButton @login-success="handleLoginSuccess"/>
                </div>
            </div>

            <div v-if="user && device && !loading" class="checkout-success">
                <h2>✅ Successfully checked out!</h2>
                <p><strong>Device:</strong> {{ device.model }}</p>
                <p><strong>PIN:</strong> <span class="pin-number">{{ device.pin }}</span></p>
                <p><strong>Checked out by:</strong> {{ user.email }}</p>
            </div>

            <LoadingSkeleton v-if="user && loading" />

            <div v-if="user && !loading && !device" class="no-device">
                <p>Device not found</p>
            </div>
        </div>
    </PageLayout>
</template>

<script setup>
import {ref, onMounted} from "vue";
import {useRoute} from "vue-router";
import {auth, provider} from "../firebase";
import {signInWithPopup, onAuthStateChanged} from "firebase/auth";
import {getDeviceById, updateDevice} from "../services/deviceService";
import {getFunctions, httpsCallable} from "firebase/functions";
import {app} from "../firebase";
import {COLORS, FONTS, GRADIENTS} from "@/styles/brand";
import PageLayout from "./PageLayout.vue";
import LoginButton from "./LoginButton.vue";
import LoadingSkeleton from "./LoadingSkeleton.vue";

const route = useRoute();
const user = ref(null);
const device = ref(null);
const loading = ref(false);

const updateDeviceInfo = async (loggedInUser) => {
    if (device.value) {
        const isOwner = device.value.owner === loggedInUser.email;
        await updateDevice(device.value.id, {
            lastTakenBy: isOwner ? '' : loggedInUser.email,
            lastTakenAt: new Date(),
            totalHours: device.value.totalHours || 0,
            checkoutCount: device.value.checkoutCount || 0
        });

        // Send Slack notification only if not owner
        if (!isOwner) {
            setTimeout(async () => {
                try {
                    const idToken = await loggedInUser.getIdToken();
                    const functions = getFunctions(app, 'us-central1');
                    const sendSlackNotification = httpsCallable(functions, 'sendSlackNotification');
                    await sendSlackNotification({
                        userEmail: loggedInUser.email,
                        deviceModel: device.value.model,
                        deviceId: device.value.id
                    });
                } catch (error) {
                    console.error('Failed to send Slack notification:', error);
                }
            }, 3000);
        }
    }
};

const handleLoginSuccess = async (loggedInUser) => {
    user.value = loggedInUser;
    loading.value = true;
    device.value = null;

    const deviceId = route.params.deviceId;
    if (deviceId) {
        try {
            device.value = await getDeviceById(deviceId);
            if (device.value) {
                await updateDeviceInfo(loggedInUser);
            }
        } catch (error) {
            console.error('Error fetching device:', error);
        }
    }
    loading.value = false;
};

onMounted(async () => {
    // Only check for existing auth, don't fetch device here
    onAuthStateChanged(auth, async (currentUser) => {
        if (currentUser && currentUser.email.endsWith('@nordeus.com') && !user.value) {
            // Only auto-login if no user is set (page refresh case)
            user.value = currentUser;
            loading.value = true;

            const deviceId = route.params.deviceId;
            if (deviceId) {
                try {
                    device.value = await getDeviceById(deviceId);
                    if (device.value) {
                        await updateDeviceInfo(currentUser);
                    }
                } catch (error) {
                    console.error('Error fetching device:', error);
                }
            }
            loading.value = false;
        }
    });
});
</script>

<style scoped>
.checkout-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 20px;
    position: relative;
    z-index: 1;
}

.device-preview, .checkout-success, .no-device, .login-required {
    background: v-bind('GRADIENTS.CARD');
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 40px;
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    text-align: center;
    max-width: 450px;
    color: v-bind('COLORS.PRIMARY.WHITE');
    position: relative;
    z-index: 1;
    animation: fadeInScale 0.6s ease-out;
}



.device-preview h2, .checkout-success h2, .no-device h2, .login-required h2 {
    background: v-bind('GRADIENTS.ACCENT');
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 24px;
    font-family: v-bind('FONTS.PRIMARY');
}

.checkout-success h2 {
    background: none;
    -webkit-background-clip: unset;
    -webkit-text-fill-color: unset;
    background-clip: unset;
    color: v-bind('COLORS.PRIMARY.WHITE');
}

.pin-number {
    background: v-bind('COLORS.PRIMARY.RED');
    color: v-bind('COLORS.PRIMARY.WHITE');
    padding: 6px 12px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 1.2rem;
}

.device-preview p, .checkout-success p {
    color: rgba(255, 255, 255, 0.8);
    margin: 12px 0;
    font-size: 1.1rem;
}

.device-preview strong, .checkout-success strong {
    color: v-bind('COLORS.PRIMARY.WHITE');
    font-weight: 600;
}

.login-button-container {
    display: flex;
    justify-content: center;
    margin-top: 24px;
}


.checkout-success {
    border-color: v-bind('COLORS.SECONDARY.GREEN + "80"');
}

.no-device {
    border-color: v-bind('COLORS.PRIMARY.RED + "80"');
}

@keyframes fadeInScale {
    from {
        opacity: 0;
        transform: scale(0.9) translateY(20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}
</style>
